import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listAdminOrders, purgeAdminTestOrders } from '../../../services/adminApi';
import useConfirm from '../shell/useConfirm';
import OrderDrawer, { TON_GROUPE, argent } from './OrderDrawer';
import { Tag } from '../catalog/ui';
import '../catalog/catalog.css';
import './orders.css';

// Commandes : liste filtrable (statut, fournisseur), recherche (numéro, nom, e-mail, livre), fiche en panneau latéral.
// Tout est dans l'adresse (?statut=echec&fournisseur=prodigi&q=…&commande=…) : un lien partageable, un retour arrière qui fonctionne.

const GROUPES = ['echec', 'preparation', 'fabrication', 'expediee', 'livree', 'paiement', 'annulee'];
const FOURNISSEURS = ['gelato', 'prodigi', 'aucun'];

export default function OrdersSection() {
  const { t, i18n } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const statut = params.get('statut') || '';
  const fournisseur = params.get('fournisseur') || '';
  const mode = params.get('mode') || '';
  const q = params.get('q') || '';
  const page = Number(params.get('page')) || 1;
  const ouverte = params.get('commande') || '';
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [saisie, setSaisie] = useState(q);
  const derniere = useRef(0);
  const [message, setMessage] = useState('');
  const [confirmer, confirmation] = useConfirm();

  const changer = (modifs) => {
    const suivant = new URLSearchParams(params);
    Object.entries(modifs).forEach(([cle, valeur]) => { if (valeur) suivant.set(cle, valeur); else suivant.delete(cle); });
    if (!('page' in modifs) && !('commande' in modifs)) suivant.delete('page'); // un filtre repart de la première page
    setParams(suivant, { replace: true });
  };

  const charger = useCallback(async () => {
    const numero = ++derniere.current; // seule la réponse la plus récente est gardée (frappes rapides)
    try {
      const r = await listAdminOrders({ statut, fournisseur, mode, q, page });
      if (numero === derniere.current) { setData(r); setErreur(''); }
    } catch (err) {
      if (numero === derniere.current) setErreur(err?.message || t('orders.loadFailed'));
    }
  }, [statut, fournisseur, mode, q, page, t]);
  useEffect(() => { charger(); }, [charger]);

  // Recherche : le champ se met à jour tout de suite, l'adresse (donc la requête) 300 ms après la dernière frappe.
  useEffect(() => { setSaisie(q); }, [q]);
  useEffect(() => {
    if (saisie === q) return undefined;
    const minuteur = setTimeout(() => changer({ q: saisie.trim() }), 300);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saisie]);

  const compteur = (cle, valeur) => (data ? (cle === 'groupe' ? data.counts.groupes[valeur] : (data.counts.fournisseurs[valeur] || 0)) : null);
  const total = data ? Object.values(data.counts.groupes).reduce((a, b) => a + b, 0) : null;
  const premier = data ? (data.page - 1) * data.perPage + 1 : 0;
  const dernier = data ? Math.min(data.page * data.perPage, data.total) : 0;

  const purger = async () => {
    setMessage('');
    if (!(await confirmer({
      title: t('orders.modes.purgeTitle'),
      message: t('orders.modes.purgeBody', { count: data.counts.modes.test }),
      confirmLabel: t('orders.modes.purgeYes')
    }))) return;
    try {
      const r = await purgeAdminTestOrders();
      setMessage(t('orders.modes.purgeDone', { deleted: r.deleted, refused: r.refused }));
      charger();
    } catch (err) {
      setErreur(err?.message || t('orders.modes.purgeFailed'));
    }
  };

  const chip = (cle, valeur, libelle, nombre) => {
    const actif = ({ statut, fournisseur, mode })[cle] === valeur;
    return (
      <button key={`${cle}-${valeur}`} type="button" className="adm-chip" aria-pressed={actif} onClick={() => changer({ [cle]: actif ? '' : valeur })}>
        {libelle} {nombre !== null && <b>{nombre}</b>}
      </button>
    );
  };

  return (
    <>
      <form className="adm-orders-search" role="search" onSubmit={(e) => { e.preventDefault(); changer({ q: saisie.trim() }); }}>
        <label className="adm-sr" htmlFor="adm-orders-q">{t('orders.searchLabel')}</label>
        <input id="adm-orders-q" className="adm-input" type="search" value={saisie} placeholder={t('orders.searchPlaceholder')} onChange={(e) => setSaisie(e.target.value)} />
      </form>

      <div className="adm-chips" role="group" aria-label={t('orders.filters')}>
        <button type="button" className="adm-chip" aria-pressed={!statut && !fournisseur && !mode} onClick={() => changer({ statut: '', fournisseur: '', mode: '' })}>
          {t('orders.all')} {total !== null && <b>{total}</b>}
        </button>
        {GROUPES.map((g) => chip('statut', g, t(`orders.groups.${g}`), compteur('groupe', g)))}
        <span className="adm-chips-sep" aria-hidden="true" />
        {FOURNISSEURS.map((f) => chip('fournisseur', f, t(`orders.providers.${f}`), compteur('fournisseur', f)))}
        <span className="adm-chips-sep" aria-hidden="true" />
        {chip('mode', 'reel', t('orders.modes.reel'), data ? data.counts.modes.reel : null)}
        {chip('mode', 'test', t('orders.modes.test'), data ? data.counts.modes.test : null)}
      </div>

      {data && data.counts.modes.test > 0 && (
        <div className="adm-actions">
          <button type="button" className="btn btn-outline btn-sm adm-btn-danger" onClick={purger}>{t('orders.modes.purge', { count: data.counts.modes.test })}</button>
          <span className="adm-note">{t('orders.modes.purgeHelp')}</span>
        </div>
      )}
      {message && <p className="adm-note adm-flash" role="status">{message}</p>}
      {confirmation}

      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      {!data && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}

      {data && data.orders.length === 0 && <p className="adm-note">{t('orders.empty')}</p>}
      {data && data.orders.length > 0 && (
        <div className="adm-tbl">
          <table>
            <thead>
              <tr>
                <th>{t('orders.cols.order')}</th><th>{t('orders.cols.customer')}</th><th>{t('orders.cols.book')}</th><th>{t('orders.cols.provider')}</th>
                <th>{t('orders.cols.status')}</th><th className="num">{t('orders.cols.total')}</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.id} className="is-open" onClick={() => changer({ commande: o.id })}>
                  <td>
                    <button type="button" className="adm-rowbtn" onClick={(e) => { e.stopPropagation(); changer({ commande: o.id }); }}><b>{o.number || o.id.slice(0, 8)}</b></button>{o.mode === 'test' && <> <Tag tone="warn">{t('orders.modes.testTag')}</Tag></>}
                    <small>{new Date(o.createdAt).toLocaleDateString(i18n.language)}</small>
                  </td>
                  <td>{o.customer.name || '—'}<small>{o.customer.country || ''}</small></td>
                  <td>{o.book.title || '—'}<small>{o.book.format ? t(`orders.formats.${o.book.format}`, { defaultValue: o.book.format }) : ''}</small></td>
                  <td>{t(`orders.providers.${o.provider || 'aucun'}`, { defaultValue: o.provider })}</td>
                  <td><Tag tone={TON_GROUPE[o.group]}>{t(`orders.groups.${o.group}`)}</Tag></td>
                  <td className="num">{argent(o.total.cents, o.total.currency)}{o.promoCode && <small>{o.promoCode}</small>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > data.perPage && (
        <nav className="adm-pager" aria-label={t('orders.pagination')}>
          <span className="adm-note">{t('orders.range', { from: premier, to: dernier, total: data.total })}</span>
          <span className="adm-sp" />
          <button type="button" className="btn btn-outline btn-sm" disabled={data.page <= 1} onClick={() => changer({ page: String(data.page - 1) })}>{t('orders.previous')}</button>
          <button type="button" className="btn btn-outline btn-sm" disabled={dernier >= data.total} onClick={() => changer({ page: String(data.page + 1) })}>{t('orders.next')}</button>
        </nav>
      )}
      {data && data.truncated && <p className="adm-note">{t('orders.truncated')}</p>}

      {ouverte && (
        <OrderDrawer
          key={ouverte}
          id={ouverte}
          onClose={() => changer({ commande: '' })}
          onChanged={charger}
          onDeleted={() => { changer({ commande: '' }); setMessage(t('orders.drawer.deleted')); charger(); }}
        />
      )}
    </>
  );
}
